/* eslint-disable max-lines */
/* eslint-disable extra-rules/no-commented-out-code */
import isEmpty from 'lodash/isEmpty';
import { FACETS_FIELD_KEY } from '@tcp/core/src/services/abstractors/productListing/productListing.utils';
import { getAPIConfig, isClient, isServer, routerPush, routerReplace } from '@tcp/core/src/utils';
import { PROMO_FLAGS, SLASH_C_PATH, SLASH_C_ROUTE } from '../../../../../constants/plp.constants';
import { changeOnlyFirstLetterToCapital } from '../../../../../utils/utils';
import { parseBoolean } from '../../../../../utils/utils.web';
// import { getLocalStorage } from '../../../../../utils/localStorageManagement';

/*
 * Gets the available l3 categories from facets by eliminating
 * phantom categories (displayToCustomer = false) and sort them
 * as per navigation response
 */
const getAvailableL3Category = (facets, currentCategory) => {
  const availableL3Cats = facets?.multilevel?.bucket?.[0]?.values;
  const availableL3Cat = [];
  if (!Array.isArray(availableL3Cats)) return availableL3Cat;

  const l3CategoryObj = availableL3Cats.reduce((categoryObj, category) => {
    return { ...categoryObj, [category.id]: category };
  }, {});
  currentCategory.subCategories.forEach((l3Bucket) => {
    if (
      l3Bucket?.categoryContent?.displayToCustomer &&
      l3CategoryObj[l3Bucket?.categoryContent?.id]
    ) {
      availableL3Cat.push(l3CategoryObj[l3Bucket?.categoryContent?.id]);
    }
  });
  return availableL3Cat;
};

export const getHrefLangUri = () => {
  const { hrefLangURL, hrefLangPrefix } = getAPIConfig();
  return `${hrefLangURL}/${hrefLangPrefix}`;
};

const getCategoryObj = (start, end, l3FacetData, pageNumber) => {
  return {
    start,
    end,
    rows: end - start + 1,
    pageNumber,
    ...(l3FacetData || {}),
  };
};

const getBucketingEndCount = (l3ProductsLeft, productsNeeded, start) => {
  return l3ProductsLeft > productsNeeded ? start + productsNeeded : start + l3ProductsLeft - 1;
};

/*
 * Contruct the page number mapping for l2 categories by
 * extrating the number of products to be displayed per page
 */
export const getL2BucketingByPageNumber = (facets, recordsPerPage, currentCategory) => {
  let unbxdCallByPageNumber = [];
  let totalProductsCount = 0;
  const availableL3Cat = getAvailableL3Category(facets, currentCategory);
  if (!Array.isArray(availableL3Cat)) return { unbxdCallByPageNumber, totalProductsCount };

  let productsBucket = [];
  let totalProductsInBucket = 0;

  availableL3Cat.forEach((l3Category, index) => {
    const { count, ...l3FacetData } = l3Category || {};
    totalProductsCount += count;
    let l3ProductsLeft = count;
    let start = 0;
    let end = recordsPerPage - 1;
    while (l3ProductsLeft > 0) {
      const pageNumber = unbxdCallByPageNumber.length;
      if (l3ProductsLeft >= recordsPerPage && !totalProductsInBucket) {
        unbxdCallByPageNumber.push(getCategoryObj(start, end, l3FacetData, pageNumber));
        l3ProductsLeft -= recordsPerPage;
        start = end + 1;
        end = start + recordsPerPage - 1;
      } else if (!totalProductsInBucket) {
        end = start + l3ProductsLeft - 1;
        productsBucket.push(getCategoryObj(start, end, l3FacetData, pageNumber));
        totalProductsInBucket = l3ProductsLeft;
        l3ProductsLeft = 0;
      } else {
        const productsNeeded = recordsPerPage - totalProductsInBucket - 1;
        end = getBucketingEndCount(l3ProductsLeft, productsNeeded, start);
        productsBucket.push(getCategoryObj(start, end, l3FacetData, pageNumber));
        const productsFetched = end - start + 1;
        totalProductsInBucket += productsFetched;
        l3ProductsLeft -= productsFetched;
        start = end + 1;
        end = start + recordsPerPage - 1;
      }
      if (
        totalProductsInBucket === recordsPerPage ||
        (availableL3Cat.length === index + 1 && productsBucket.length)
      ) {
        unbxdCallByPageNumber = [...unbxdCallByPageNumber, productsBucket];
        productsBucket = [];
        totalProductsInBucket = 0;
      }
    }
  });
  return { unbxdCallByPageNumber, totalProductsCount };
};

export const getUnbxdCallByPageNumber = (totalProducts, recordsPerPage) => {
  const endProdCount = recordsPerPage - 1;
  const unbxdCallByPageNumber = [];
  let start = 0;
  let end = endProdCount;
  let unbxdCallsNeed = Math.ceil(totalProducts / recordsPerPage);
  while (unbxdCallsNeed > 0) {
    unbxdCallByPageNumber.push({
      start,
      end,
      rows: recordsPerPage,
      pageNumber: unbxdCallByPageNumber.length,
    });
    start = end + 1;
    end = start + endProdCount;
    unbxdCallsNeed -= 1;
  }
  return { unbxdCallByPageNumber, totalProductsCount: totalProducts };
};

const generateQuery = (nameAry, tmpObj, keyObj, isCleanUrlForSizeEnabled) => {
  const queries = nameAry?.reduce((acc, name) => {
    if (tmpObj[name]?.length > 0) {
      const key =
        isServer() && keyObj[name] === 'v_clothingsize_uFilter' && isCleanUrlForSizeEnabled
          ? 'v_clothingsizefacet_uFilter'
          : keyObj[name];
      acc.push(`${key}=${tmpObj[name].join(',')}`);
    }
    return acc;
  }, []);

  return queries.join('&');
};

const extractValuesFromUrl = (url1, nameAry) => {
  const result = {};

  const getSubstring = (url, name) => {
    const nameLength = name?.length;
    const filterStartIndex = url.indexOf(`/${name}_`);
    if (filterStartIndex !== -1) {
      const filterEndIndex = url.indexOf('/', filterStartIndex + 1);
      if (filterEndIndex !== -1) {
        return url.substring(filterStartIndex + nameLength + 2, filterEndIndex);
      }
      return url.substring(filterStartIndex + nameLength + 2);
    }
    return '';
  };

  nameAry?.forEach((name) => {
    let tmpStr = getSubstring(url1, name);
    if (name?.toLowerCase() === 'color') {
      tmpStr = tmpStr?.toUpperCase();
    }
    if (tmpStr) {
      result[name] = tmpStr.split('_').map((item) => decodeURIComponent(item));
    } else {
      result[name] = [];
    }
  });

  return result;
};

export const pushToTmpNameAry = (tmpNameAry, filteredSizeMap, sizeVal) => {
  if (filteredSizeMap?.length > 0) {
    if (isServer()) {
      tmpNameAry.push(filteredSizeMap[0]?.cleanSizeFacetForUrl);
    } else {
      tmpNameAry.push(filteredSizeMap[0]?.id);
    }
  } else {
    tmpNameAry.push(sizeVal);
  }
};

export const pathToQuery = (path, seoFacetedNavigationConfig, cleanUrlObj) => {
  const nameAry = [];
  const keyObj = {};
  const fitList = ['husky', 'slim', 'plus', 'regular'];
  const { isCleanUrlForSizeEnabled, clothingSizeMap } = cleanUrlObj || {};
  if (seoFacetedNavigationConfig && Array.isArray(seoFacetedNavigationConfig)) {
    seoFacetedNavigationConfig?.forEach((obj) => {
      const { name, key } = obj;
      nameAry.push(name);
      keyObj[name] = key;
    });
  }
  const tmpObj = extractValuesFromUrl(path, nameAry);
  if (isCleanUrlForSizeEnabled && tmpObj?.size?.length > 0) {
    const tmpNameAry = [];
    tmpObj.size.forEach((sizeVal) => {
      const filteredSizseMap = clothingSizeMap?.filter(
        (sizeMap) => sizeMap.cleanSizeFacetForUrl === sizeVal
      );
      pushToTmpNameAry(tmpNameAry, filteredSizseMap, sizeVal);
    });

    tmpObj.size = tmpNameAry;
    if (fitList?.includes(tmpObj.size[tmpObj.size.length - 1]?.toLowerCase())) {
      tmpObj.fit.push(tmpObj.size[tmpObj.size.length - 1]);
      tmpObj.size.pop();
    }
  }

  const query = generateQuery(nameAry, tmpObj, keyObj, isCleanUrlForSizeEnabled);
  if (query) {
    return encodeURIComponent(query);
  }
  return '';
};

const filterKeys = (
  keysAry,
  extractedValues,
  keyObj,
  isCleanUrlForSizeEnabled,
  clothingSizeMap,
  filters
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  if (!keysAry) {
    return { ...filters };
  }
  const fitList = ['husky', 'slim', 'plus', 'regular'];
  const sizeArray = extractedValues?.size;
  const lastSize = sizeArray?.[sizeArray.length - 1];

  if (fitList.includes(lastSize?.toLowerCase())) {
    extractedValues?.fit?.push(changeOnlyFirstLetterToCapital(lastSize));
    sizeArray?.pop();
  }
  return keysAry.reduce(
    (newFilters, key) => {
      let updatedFilters = { ...newFilters };

      if (
        extractedValues[key]?.length > 0 &&
        isClient() &&
        keyObj[key] === 'v_clothingsize_uFilter' &&
        isCleanUrlForSizeEnabled
      ) {
        const tmpAry = isEmpty(clothingSizeMap)
          ? []
          : clothingSizeMap
              ?.filter(
                (sizeMap) =>
                  extractedValues[key]?.includes(sizeMap.cleanSizeFacetForUrl) ||
                  extractedValues[key]?.includes(sizeMap.id)
              )
              .map((sizeMap) => sizeMap.id);
        if (tmpAry.length) {
          updatedFilters = { ...updatedFilters, [keyObj[key]]: tmpAry };
        } else {
          updatedFilters = {
            ...updatedFilters,
            v_clothingsizefacet_uFilter: extractedValues[key],
          };
        }
      } else if (
        extractedValues[key]?.length > 0 &&
        isServer() &&
        keyObj[key] === 'v_clothingsize_uFilter' &&
        isCleanUrlForSizeEnabled
      ) {
        updatedFilters = {
          ...updatedFilters,
          v_clothingsizefacet_uFilter: extractedValues[key],
        };
      } else {
        updatedFilters = { ...updatedFilters, [keyObj[key]]: extractedValues[key] };
      }

      return updatedFilters;
    },
    { ...filters }
  );
};

export const getFilters = (
  queryParams,
  isFacetedNavigationEnabled,
  pathName,
  seoFacetedNavigationConfig,
  cleanUrlObj
) => {
  if (!queryParams && !isFacetedNavigationEnabled) return {};
  let filters = {};
  const { isCleanUrlForSizeEnabled, clothingSizeMap } = cleanUrlObj || {};
  const queryParamsArray = (queryParams && queryParams.split('&')) || [];
  if (isFacetedNavigationEnabled) {
    const nameAry = [];
    const keyObj = {};
    if (seoFacetedNavigationConfig && Array.isArray(seoFacetedNavigationConfig)) {
      seoFacetedNavigationConfig?.forEach((obj1) => {
        const { name, key } = obj1;
        nameAry.push(name);
        keyObj[name] = key;
      });
    }

    const tmpPathName = pathName?.split('?')[0];
    const extractedValues = extractValuesFromUrl(tmpPathName, nameAry);

    const keysAry = Object.keys(extractedValues);
    filters = filterKeys(
      keysAry,
      extractedValues,
      keyObj,
      isCleanUrlForSizeEnabled,
      clothingSizeMap,
      filters
    );
  }

  queryParamsArray.forEach((queryParam) => {
    const [queryKey, queryValue] = queryParam.split('=');
    const value = decodeURIComponent(queryValue);
    const key = decodeURIComponent(queryKey);

    filters[key] = key?.toLowerCase() === FACETS_FIELD_KEY.sort ? value : value.split(',');
  });
  return filters;
};

const getModifiedQueryString = (facetValue, query) => {
  return facetValue.length ? (query ? '&filter=' : '') + facetValue.join(' OR ') : '';
};

const isUnbxdFacetKey = (key) =>
  key.toLowerCase() !== FACETS_FIELD_KEY.unbxdDisplayName &&
  key.toLowerCase() !== FACETS_FIELD_KEY.sort;

// , ignoreFilter = []
export const extractFilters = (filtersAndSort) => {
  const filterQuery = {};
  let query = '';
  const facetKeys = Object.keys(filtersAndSort);
  facetKeys.forEach((facetKey) => {
    // if (!ignoreFilter.includes(facetKey)) {
    let facetValue = filtersAndSort[facetKey];
    if (
      isUnbxdFacetKey(facetKey) &&
      facetValue &&
      facetValue.length > 0 &&
      (facetKey.indexOf('uFilter') > -1 || facetKey === 'min_offer_price')
    ) {
      facetValue = facetValue.map((facet) => {
        const encodedFacet = encodeURIComponent(facet);
        if (facetKey === 'min_offer_price') {
          return `${facetKey}:[${encodedFacet}]`;
        }
        return `${facetKey}:"${encodedFacet}"`;
      });
      query += getModifiedQueryString(facetValue, query);
    } else if (facetKey === 'bopisStoreId') {
      query += getModifiedQueryString(facetValue, query);
    }
    // }
  });

  if (query !== '') filterQuery.filter = query;
  return filterQuery;
};

export const getFacetSwatchImgPath = (id) => {
  return `/wcsstore/GlobalSAS/images/tcp/category/color-swatches/${id}.gif`;
};

export const getQueryUrlWithPage = (urlQueryValues, pageNumber) => {
  return urlQueryValues.includes('page=')
    ? urlQueryValues.replace(/(page=).*?(&|$)/, `page=${pageNumber}`)
    : `${urlQueryValues}&page=${pageNumber}`;
};

export const setUpdatedUrl = (pageType, cid, urlQueryValues, pageNumber) => {
  const routeUrl = urlQueryValues
    ? `/${pageType}/${cid}?${getQueryUrlWithPage(urlQueryValues, pageNumber)}`
    : `/${pageType}/${cid}?page=${pageNumber}`;
  // Update the url in browser based on the filters applied
  routerPush(`${SLASH_C_PATH}cid=${cid}`, routeUrl, { shallow: true }, undefined, {
    scroll: false,
  });
  return routeUrl;
};

export const setUpdatedUrlSeo = (pageType, cid, urlQueryValues, pageNumber, urlParam) => {
  const routeUrl = urlQueryValues
    ? `/${pageType}/${cid}/${urlParam}?${getQueryUrlWithPage(urlQueryValues, pageNumber)}`
    : `/${pageType}/${cid}/${urlParam}?page=${pageNumber}`;

  routerPush(`${SLASH_C_PATH}cid=${cid}`, routeUrl, { shallow: true }, undefined, {
    scroll: false,
  });
  return routeUrl;
};

export const getPlpUrlQueryValues = (filtersAndSort, pathName, isSortOrFilter, pageNumber) => {
  if (!isSortOrFilter) {
    return pathName.substr(pathName.indexOf(SLASH_C_ROUTE));
  }

  let urlQueryValues = '';
  if (filtersAndSort) {
    // const appliedFilters = evalAppliedFilters(filtersAndSort);

    Object.keys(filtersAndSort).forEach((key) => {
      if (filtersAndSort[key].length) {
        const filterParam =
          key === FACETS_FIELD_KEY.sort
            ? `${encodeURIComponent(key)}=${encodeURIComponent(filtersAndSort[key])}`
            : `${encodeURIComponent(key)}=${encodeURIComponent(filtersAndSort[key].join(','))}`;
        urlQueryValues = urlQueryValues ? `${urlQueryValues}&${filterParam}` : filterParam;
      }
    });
  }

  const [, , pageType, cid] = pathName.split('/');
  return setUpdatedUrl(pageType, cid, urlQueryValues, pageNumber);
};

export const handleOtherFilters = (key, value) => {
  if (key === FACETS_FIELD_KEY.sort) {
    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }
  return `${encodeURIComponent(key)}=${encodeURIComponent(value.join(','))}`;
};

export const mergeValues = (obj) => {
  return Object.entries(obj)
    .sort(([keyA], [keyB]) => keyA - keyB) // sort keys in ascending order
    .map(([, value]) => value) // get values
    .join('/'); // join values with '/'
};

export const updateUrlParam = (urlParam, sequence, name, filters, cleanUrlObj) => {
  let valueToAdd = `${name}_${filters.join('_')}`;
  if (name?.toLowerCase() === 'color') {
    valueToAdd = valueToAdd?.toLowerCase();
  }
  if (
    name?.toLowerCase() === 'size' &&
    cleanUrlObj?.isCleanUrlForSizeEnabled &&
    cleanUrlObj?.clothingSizeMap?.length > 0
  ) {
    const tempAry = [];
    cleanUrlObj?.clothingSizeMap?.forEach((obj) => {
      if (filters?.includes(obj.id)) {
        if (obj.cleanSizeFacetForUrl) {
          tempAry.push(obj.cleanSizeFacetForUrl);
        } else {
          tempAry.push(obj.id);
        }
      }
    });
    valueToAdd = `${name}_${tempAry.join('_')}`;
  }

  return {
    ...urlParam,
    [sequence]: urlParam[sequence] ? `${urlParam[sequence]}${valueToAdd}` : valueToAdd,
  };
};

export const filterConfig = (key, filtersAndSort, seoFacetedNavigationConfig) => {
  let tempObj = null;
  if (
    key !== 'v_tcpfit_unbxd_uFilter' ||
    (key === 'v_tcpfit_unbxd_uFilter' && filtersAndSort?.v_clothingsize_uFilter?.length > 0)
  ) {
    tempObj = seoFacetedNavigationConfig?.filter(
      (obj) => obj?.key?.toLowerCase() === key?.toLowerCase()
    );
  }
  return tempObj;
};

export const getPlpUrlQueryValuesSeo = (
  filtersAndSort,
  pathName,
  isSortOrFilter,
  pageNumber,
  seoFacetedNavigationConfig,
  cleanUrlObj
) => {
  if (!isSortOrFilter) {
    return pathName.substr(pathName.indexOf(SLASH_C_ROUTE));
  }

  let urlQueryValues = '';
  let urlParam = {};
  if (filtersAndSort) {
    Object.keys(filtersAndSort).forEach((key) => {
      let tempObj = {};

      tempObj = filterConfig(key, filtersAndSort, seoFacetedNavigationConfig);

      if (filtersAndSort[key]?.length) {
        let filterParam = '';
        if (tempObj?.length > 0) {
          const { name, sequence } = tempObj[0];
          urlParam = updateUrlParam(urlParam, sequence, name, filtersAndSort[key], cleanUrlObj);
        } else {
          filterParam = handleOtherFilters(key, filtersAndSort[key]);
        }
        urlQueryValues = urlQueryValues ? `${urlQueryValues}&${filterParam}` : filterParam;
      }
    });
  }
  // merging size & fit values
  if (urlParam['3']) {
    const fitVal = urlParam['3']?.split('_')[1];
    urlParam['2'] = `${urlParam['2']}_${fitVal?.toLowerCase()}`;
    delete urlParam['3'];
  }
  const [, , pageType, cid] = pathName.split('/');
  return setUpdatedUrlSeo(pageType, cid, urlQueryValues, pageNumber, mergeValues(urlParam));
};

const getIsBopisFilterOn = (filters) => {
  return filters && filters.bopisStoreId ? filters.bopisStoreId.length > 0 : false;
};

export const getFiltersPayload = (pathName, filters) => {
  const [asPath, query] = pathName.split('?');
  const filtersFromUrl = filters || getFilters(query);
  const facetsPayload = isEmpty(filtersFromUrl) ? {} : extractFilters(filtersFromUrl);
  if (filtersFromUrl && filtersFromUrl.sort) {
    facetsPayload.sort = filtersFromUrl.sort;
  }
  const isBopisFilterOn = getIsBopisFilterOn(filtersFromUrl);
  return { asPath, facetsPayload, filtersFromUrl, isBopisFilterOn };
};

export const getFiltersPayloadSeo = (
  pathName,
  filters,
  isFacetedNavigationEnabled,
  seoFacetedNavigationConfig,
  cleanUrlObj
) => {
  const [asPath, query] = pathName?.split('?');
  const filtersFromUrl =
    filters ||
    getFilters(
      query,
      isFacetedNavigationEnabled,
      pathName,
      seoFacetedNavigationConfig,
      cleanUrlObj
    );

  const facetsPayload = isEmpty(filtersFromUrl) ? {} : extractFilters(filtersFromUrl);

  if (filtersFromUrl && filtersFromUrl.sort) {
    facetsPayload.sort = filtersFromUrl.sort;
  }
  const isBopisFilterOn = getIsBopisFilterOn(filtersFromUrl);
  return { asPath, facetsPayload, filtersFromUrl, isBopisFilterOn };
};

export const getFilterCountFromURL = (obj) => {
  if (!isEmpty(obj)) {
    return Object.entries(obj).reduce((count, [, value]) => {
      return Array.isArray(value) && value.length > 0 ? count + value.length : count;
    }, 0);
  }
  return 0;
};

export const getAppliedFilters = (filters = {}, filterIds = {}) => {
  const appliedFilters = {};
  const facetKeys = filterIds ? Object.keys(filterIds) : [];

  facetKeys.forEach((facetKey) => {
    if (isUnbxdFacetKey(facetKey)) {
      // for facets having facetName as key
      if (facetKey === 'bopisStoreId') {
        appliedFilters[facetKey] = filterIds[facetKey];
      } else if (facetKey === 'min_offer_price') {
        appliedFilters[facetKey] = filterIds[facetKey];
      } else if (facetKey === 'filter_cat_grp') {
        appliedFilters[facetKey] = filterIds[facetKey];
      } else {
        appliedFilters[facetKey] = !filters[facetKey]
          ? []
          : filters[facetKey]
              .filter((item) => filterIds[facetKey].indexOf(item.id) > -1)
              .map((item) => item.id);
      }
    }
  });
  return appliedFilters;
};

export const getImageForSEO = (paginatedProducts) => {
  if (Array.isArray(paginatedProducts)) {
    const stylePartNo = paginatedProducts[0] && paginatedProducts[0].style_partno;
    const productImage = paginatedProducts[0] && paginatedProducts[0].productimage;
    return `${stylePartNo}/${productImage}` || ' ';
  }
  return '';
};

export const getImageForSEOFromCarousalData = (carouselData, matchPath) => {
  const match = carouselData?.find((obj) => obj.linkUrl?.split('=')[1] === matchPath);
  return match && match.url;
};

export const l3Check = (breadCrumbs) => {
  const { hasL3, catDepth } = breadCrumbs[breadCrumbs.length - 1];
  if (catDepth !== 'l2') return true;
  return hasL3;
};

export const navigationToLastPage = (pathName, lastPage) => {
  const [asPath, queryParams] = pathName.split('?');
  const [, , pageType, cid] = asPath.split('/');
  let navigationRoute = `/${pageType}/${cid}`;
  const params = queryParams.split('&');
  const queryParamsWithoutPage = params.filter((value) => !value.includes('page='));
  const updatedQury = queryParamsWithoutPage.join('&');
  if (lastPage <= 1 && updatedQury) {
    navigationRoute = `${navigationRoute}?${updatedQury}`;
  } else if (updatedQury) {
    navigationRoute = `${navigationRoute}?${updatedQury}&page=${lastPage}`;
  } else if (lastPage > 1) {
    navigationRoute = `${navigationRoute}?page=${lastPage}`;
  }
  routerReplace(`/${pageType}?cid=${cid}`, navigationRoute, { shallow: true }, undefined, {
    scroll: false,
  });
};

export const generateGroups = (level1) => {
  try {
    let level2Groups = [];
    const groupings = {};

    // Ensure level1.subCategories is an object
    const subCategories = level1.subCategories || {};

    const listOfGroups = Object.keys(subCategories);
    // for each L2 parse and place in proper group
    listOfGroups.forEach((grp) => {
      if (subCategories[grp] && subCategories[grp].items) {
        subCategories[grp].items.forEach((L2) => {
          const groupName = grp;
          const groupOrder = subCategories[grp].order;

          // if new grouping initialize array
          if (!groupings[groupName]) {
            groupings[groupName] = {
              order: groupOrder,
              menuItems: [],
            };
          }

          // Push L2 in this bucket
          groupings[groupName].menuItems.push(L2);
        });
      }
    });

    // Now get all groups and generate array of object, this is not too bad as there are at most 3-4 groups
    level2Groups = Object.keys(groupings).map((group) => ({
      groupName: group,
      order: groupings[group].order,
      menuItems: groupings[group].menuItems,
    }));

    return level2Groups.sort((prevGroup, curGroup) => {
      return prevGroup.order - curGroup.order;
    });
  } catch (error) {
    console.error('getHeaderNavigationTree:generateGroups', error);
    return [];
  }
};

const getOrganizedHeaderNavigationTree = (unorganizedTree) => {
  return (
    unorganizedTree &&
    unorganizedTree.map((L1) => {
      return {
        ...L1,
        menuGroupings: generateGroups(L1),
      };
    })
  );
};

export const findInL2 = (currentCategory, subCategories) => {
  const subGroups = subCategories ? Object.keys(subCategories) : [];
  return subGroups.find((group) =>
    subCategories[group].items.find(
      (L2) => L2 && L2.asPath === currentCategory && !L2.categoryContent.linkedCategory
    )
  );
};

export const findInL3 = (currentCategory, subCategories) => {
  const subGroups = subCategories ? Object.keys(subCategories) : [];
  return subGroups.find((group) =>
    subCategories[group].items.find(
      (L2) =>
        L2.subCategories &&
        L2.subCategories.find(
          (item) => item && item.asPath === currentCategory && !item.categoryContent.linkedCategory
        )
    )
  );
};

export const getNavigationTree = (navigationData, pathName) => {
  if (!pathName) {
    return null;
  }
  // remove string after last slashes
  const [pathWithoutQuery] = pathName.split('?');
  const [, , , cid] = pathWithoutQuery.split('/');
  // NOTE: aspath in navigation will always be /c, so no need to configure below line for /c or /category.
  const currentCategory = `/c/${cid}`;
  const navTree = getOrganizedHeaderNavigationTree(navigationData);
  if (!navTree) {
    return navTree;
  }

  return (
    navTree.find(
      (l1) =>
        l1 &&
        l1.categoryContent &&
        l1.categoryContent.asPath === currentCategory &&
        !l1.categoryContent.linkedCategory
    ) ||
    navTree.find((l1) => findInL2(currentCategory, l1.subCategories)) ||
    navTree.find((l1) => findInL3(currentCategory, l1.subCategories))
  );
};

const getTotalCategoryContentData = (subCategoryData, totalCategoryContentData) => {
  let totalData = totalCategoryContentData;
  subCategoryData?.forEach((item) => {
    if (item?.items) {
      totalData = [...totalData, ...item.items];
    }
  });
  totalData?.forEach((item) => {
    if (item.hasL3) {
      totalData = [...totalData, ...item?.subCategories];
    }
  });
  return totalData;
};

// eslint-disable-next-line complexity
export const getplpHlpAplpChecks = (
  processedBreadCrumbs,
  navigationData,
  navTree,
  isAlternatePlpEnabled,
  isHorizontalPlpEnabled,
  isOutfitPLPEnabled,
  fromSaga,
  collectionOutfitCatID = '',
  HPLPCategories = [],
  isStageHoldCategoriesOosEnabled
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const breadCrumbData = processedBreadCrumbs?.[processedBreadCrumbs.length - 1];
  const currentCategoryId = !fromSaga ? breadCrumbData?.categoryId : breadCrumbData?.id;
  const l1CategoryContentData = navigationData?.filter(
    (item) => item?.categoryContent?.id === currentCategoryId
  );
  const aplpValue = 'MULTI IMAGE';
  const hplpValue = 'CAROUSEL';
  let isStageHoldCategory =
    isStageHoldCategoriesOosEnabled &&
    parseBoolean(l1CategoryContentData[0]?.categoryContent?.stageHoldCategory);
  let isAplpCategoryEnabled =
    isAlternatePlpEnabled && l1CategoryContentData?.[0]?.categoryContent?.productTile === aplpValue;
  let isHplpCategoryEnabled =
    isHorizontalPlpEnabled &&
    (l1CategoryContentData?.[0]?.categoryContent?.viewType === hplpValue ||
      HPLPCategories?.includes(currentCategoryId));
  const categoryLevel = fromSaga ? breadCrumbData?.catDepth === 'l1' : breadCrumbData?.length === 2;
  if (fromSaga && categoryLevel) {
    return {
      isAplpCategoryEnabled,
      isHplpCategoryEnabled,
      isStageHoldCategory,
    };
  }
  let totalCategoryContentData = [];
  const navTreeData = navTree?.subCategories;
  const subCategoryData = navTreeData && Object.values(navTreeData);
  totalCategoryContentData = getTotalCategoryContentData(subCategoryData, totalCategoryContentData);
  const currentMainCategoryData = totalCategoryContentData?.filter(
    (item) => item?.categoryContent?.id === currentCategoryId
  );
  const activeCategoryContent = currentMainCategoryData?.[0]?.categoryContent;

  if (fromSaga) {
    isAplpCategoryEnabled =
      isAlternatePlpEnabled && activeCategoryContent?.productTile === aplpValue;

    isHplpCategoryEnabled =
      isHorizontalPlpEnabled &&
      (activeCategoryContent?.viewType === hplpValue ||
        HPLPCategories?.includes(currentCategoryId));
    isStageHoldCategory =
      isStageHoldCategoriesOosEnabled && parseBoolean(activeCategoryContent?.stageHoldCategory);
    return {
      isAplpCategoryEnabled,
      isHplpCategoryEnabled,
      isStageHoldCategory,
    };
  }
  const sbcTypeValue =
    activeCategoryContent?.carouselType ||
    l1CategoryContentData?.[0]?.categoryContent?.carouselType;

  const checkOutfit =
    isOutfitPLPEnabled &&
    (activeCategoryContent?.outfitsPlp === 'TRUE' ||
      l1CategoryContentData?.[0]?.categoryContent?.outfitsPlp === 'TRUE' ||
      collectionOutfitCatID.split(',').includes(currentCategoryId));

  return {
    checkOutfit,
    sbcTypeValue,
  };
};

export const showNewCarousel = (
  plpTopPromos,
  isPLPCarouselEnabled,
  genericCarouselData,
  sbcTypeValue
) => {
  const { codeBaseCarousel, defaultCarousel } = PROMO_FLAGS;
  return (
    isPLPCarouselEnabled &&
    (sbcTypeValue === codeBaseCarousel || sbcTypeValue === defaultCarousel || !sbcTypeValue) &&
    genericCarouselData &&
    genericCarouselData.carouselData &&
    genericCarouselData.carouselData.length > 1
  );
};

export const getShowCmsCarouselFlag = (showGenericCarousel, sbcTypeValue, plpTopPromos) => {
  const { cmsBasedCarousel } = PROMO_FLAGS;
  return (
    !showGenericCarousel &&
    (sbcTypeValue === cmsBasedCarousel || sbcTypeValue === 'DEFAULT' || !sbcTypeValue) &&
    plpTopPromos &&
    plpTopPromos.length > 0
  );
};

export const getIsVariation3OutfitCarousel = (
  isGenericSBCRedesignEnabled,
  showGenericCarousel,
  plpTopPromos
) => {
  let isVariation3OutfitCarousel = false;

  if (showGenericCarousel && isGenericSBCRedesignEnabled) {
    isVariation3OutfitCarousel = true;
  } else if (!showGenericCarousel) {
    const outfitCarouselPromo =
      plpTopPromos?.length && plpTopPromos.find((promo) => promo.moduleName === 'outfitCarousel');
    if (outfitCarouselPromo && outfitCarouselPromo.moduleClassName.includes('variation3-true')) {
      isVariation3OutfitCarousel = true;
    }
  }
  return isVariation3OutfitCarousel;
};

export const getPlpPayload = (payload) => {
  const { bootstrapPayload, ...extraPayload } = payload;
  const { apiConfig } = bootstrapPayload?.payload?.apiConfig || {};
  return {
    bootstrapPayload,
    plpPayload: {
      ...extraPayload,
      apiConfig: apiConfig || getAPIConfig(),
    },
  };
};

export const resolveCategoryCall = (resolveFn, isValidCategory) => {
  if (resolveFn) resolveFn(isValidCategory);
};

export const parseContentData = (banners) => {
  try {
    return { bannerInfo: JSON.parse(banners && banners[0]?.bannerHtml) || {} };
  } catch (error) {
    return { bannerInfo: {} };
  }
};

export const getIsL2Category = (currentCategory, facetsPayload, isL3CallsWithSortAllowed) => {
  if (isL3CallsWithSortAllowed) {
    return currentCategory?.catDepth === 'l2' && currentCategory.hasL3;
  }
  return currentCategory?.catDepth === 'l2' && !facetsPayload.sort ? currentCategory.hasL3 : false;
};

export const getIsPromoCarouselToBerendered = (plpTopPromos = []) => {
  const modulesContainingCarousel = ['outfitCarousel', 'Jeans'];
  if (plpTopPromos.length > 0) {
    return plpTopPromos.some((promo) => modulesContainingCarousel.includes(promo.moduleName));
  }
  return false;
};

/**
 * Extracts the page number from a URL path.
 *
 * @param {string} pathName - The URL path.
 * @returns {number} The page number, or 1 if no page number is found.
 */
const getPageNumberFromPath = (pathName) => {
  const [, queryParams] = pathName.split('?');
  const urlParams = new URLSearchParams(queryParams);
  return urlParams.get('page') || 1;
};

/**
 * Calculates the number of products required for a specific page.
 *
 * @param {number} totalProductsCount - The total number of products.
 * @param {number} recordsPerPage - The number of records per page.
 * @param {number} pageNumber - The page number.
 * @returns {number} The number of products required for the page.
 */
const getRequiredProductCountForPage = (totalProductsCount, recordsPerPage, pageNumber) => {
  let requiredProducts;
  if (pageNumber * recordsPerPage <= totalProductsCount) {
    requiredProducts = recordsPerPage;
  } else {
    requiredProducts = totalProductsCount - recordsPerPage * (pageNumber - 1);
  }
  return requiredProducts;
};

/**
 * Calculates the number of "skeleton" products to display while the actual products are loading.
 *
 * @param {number} totalProductsCount - The total number of products.
 * @param {number} recordsPerPage - The number of records per page.
 * @param {number} firstLoadPLPProductCount - The number of products loaded on the first page load.
 * @param {string} pathName - The URL path.
 * @returns {number} The number of "skeleton" products to display.
 */
export const calculateSkeletonCount = (
  totalProductsCount,
  recordsPerPage,
  firstLoadPLPProductCount,
  pathName
) => {
  const pageNumber = getPageNumberFromPath(pathName);
  const requiredProductsCount = getRequiredProductCountForPage(
    totalProductsCount,
    recordsPerPage,
    pageNumber
  );
  const skeletonCount = requiredProductsCount - firstLoadPLPProductCount;
  if (skeletonCount > 0) {
    return skeletonCount;
  }
  return 0;
};

/**
 * Extracts the value of the "cid" query parameter from a given input string.
 * @param {string} inputString - The input string containing the query parameter. ex: "c?cid=some value";
 * @returns {string|null} The value of the "cid" parameter, or null if not found or an error occurs...
 */
export function extractCidValue(inputString) {
  try {
    // Use a regular expression to find the value after "cid="
    const match = inputString.match(/cid=([^&]+)/);
    if (match) {
      return match[1];
    }
    return null; // Return null if "cid" parameter is not found
  } catch (error) {
    return null; // Return null if an error occurs
  }
}

/**
 *
 * @param {string}
 * @returns string
 * ex: inputString : toddler-girl-school-uniforms?viaModule=promoFilter, returns toddler-girl-school-uniforms
 */
function removeQueryString(inputString) {
  const parts = inputString.split('?');
  const part2 = parts[0].split('/');
  if (part2.length > 0) {
    return part2[part2.length - 1];
  }
  return parts[0];
}

function removeQueryStringNew(url) {
  const tmpParts = url?.split('?');
  const parts = tmpParts[0]?.split('/c/');
  if (parts?.length > 1) {
    return parts[1]?.split('/')[0];
  }
  return null;
}

/**
 * This function make sure asPath and processedBreadCrumbs holds same category slug
 */
export const isSameCategory = (asPath, processedBreadCrumbs = [], isFacetedNavigationEnabled) => {
  let currentBreadCrumbPathWithoutQueryString = '';
  const currentBreadCrumbPath = extractCidValue(
    processedBreadCrumbs[processedBreadCrumbs.length - 1].pathSuffix
  );
  const pathName = isFacetedNavigationEnabled
    ? removeQueryStringNew(asPath)
    : removeQueryString(asPath);
  if (currentBreadCrumbPath) {
    currentBreadCrumbPathWithoutQueryString = removeQueryString(currentBreadCrumbPath);
  }
  return (
    currentBreadCrumbPath === pathName ||
    currentBreadCrumbPathWithoutQueryString === pathName ||
    currentBreadCrumbPath === null
  );
};

export const getESpotPlpObject = (isLevel3, category) =>
  isLevel3 ? category?.categoryContent?.mainCategory?.espotPlp : category?.mainCategory?.espotPlp;

export const getESpotCategoryID = (isLevel3, category) =>
  isLevel3 ? category?.categoryContent?.catgroupId : category?.catgroupId;

export const getESpotPlp = (category, isLevel3) => {
  const espotPlp = getESpotPlpObject(isLevel3, category);
  return espotPlp
    ? {
        id: getESpotCategoryID(isLevel3, category),
        espotContentId: espotPlp[0].contentId,
        // eslint-disable-next-line no-underscore-dangle
        typeName: espotPlp[0].__typename,
      }
    : null;
};

export const findESpotPlpPromo = ({
  breadCrumbs,
  isESpotPromoL1PlpEnabled,
  isESpotPromoL2PlpEnabled,
  isESpotPromoL3PlpEnabled,
  isESpotPromoL2PlpWithCategoryBreakEnabled,
}) => {
  if (!Array.isArray(breadCrumbs) || breadCrumbs.length === 0) {
    return null;
  }

  const lastCategory = breadCrumbs[breadCrumbs.length - 1];
  const { catDepth, hasL3, subCategories } = lastCategory;

  const isL1 = isESpotPromoL1PlpEnabled && catDepth === 'l1';
  const isL2NoL3 = isESpotPromoL2PlpEnabled && catDepth === 'l2' && !hasL3;
  const isL3 = isESpotPromoL3PlpEnabled && catDepth === 'l3';
  const isL2WithL3 = isESpotPromoL2PlpWithCategoryBreakEnabled && catDepth === 'l2' && hasL3;

  if (isL1 || isL2NoL3 || isL3) {
    return getESpotPlp(lastCategory);
  }

  if (isL2WithL3) {
    const espotPlps = (subCategories ?? [])
      .map((category) => getESpotPlp(category, true))
      .filter(Boolean); // filter out null values
    return espotPlps.length > 0 ? espotPlps : null;
  }

  return null;
};

export const findESpotById = (id, espotArray) => {
  if (!Array.isArray(espotArray) || espotArray.length === 0) {
    return null;
  }
  return espotArray.find((espot) => espot.id === id) || null;
};

export const flattenSubCategories = (node) => {
  const entries = Object.values(node?.subCategories || {});
  return entries.flatMap((entry) => {
    if (Array.isArray(entry.items)) {
      return entry.items;
    }
    if (entry.categoryContent) {
      return [entry];
    }
    return [];
  });
};

export const getFieldFromNavigation = (navTree, idPathArray, fieldName) => {
  const getField = (categoryContent) =>
    categoryContent?.[fieldName] ??
    categoryContent?.mainCategory?.[fieldName] ??
    categoryContent?.mainCategory?.set?.find((kv) => kv.key === fieldName)?.val;

  const matchCategoryById = (node, id) =>
    flattenSubCategories(node).find((item) => {
      const content = item.categoryContent || item;
      return content?.id === id || content?.catgroupId === id;
    });

  // 1. ID path traversal (short-circuits at first valid match)
  if (Array.isArray(idPathArray) && idPathArray.length > 0) {
    const isRootMatch =
      navTree?.categoryContent?.id === idPathArray[0] ||
      navTree?.categoryContent?.catgroupId === idPathArray[0];

    const finalNode = isRootMatch
      ? idPathArray.slice(1).reduce((acc, id) => (acc ? matchCategoryById(acc, id) : null), navTree)
      : null;

    const content = finalNode?.categoryContent || finalNode;
    const result = getField(content);
    if (result !== undefined) return result;
  }
  return undefined;
};

export const containsOnlyOneKey = (filterString, key) => {
  // Check if filterString is defined and is a string
  if (typeof filterString !== 'string') return false;
  // If the string contains '&filter=', split it; otherwise, use the whole string
  const filterPart = filterString.includes('&filter=')
    ? filterString.split('&filter=')[1]
    : filterString;
  // Guard clause for cases where there is no filter part
  if (!filterPart) return false;
  // Split the filter part into key-value pairs
  const filters = filterPart.split('&');
  // Check if the specified key is present and count its occurrences
  const keyCount = filters.filter((filter) => filter.startsWith(key)).length;
  // Return true if there's exactly one occurrence of the key, otherwise false
  return keyCount === 1;
};

export const hasOnlyPageParam = (path) => {
  if (!path || typeof path !== 'string') return false; // Check if path is null or not a string
  const paramsString = path.split('?')[1];
  if (!paramsString) return true; // No query parameters
  const params = new URLSearchParams(paramsString);

  // Ensure params is valid and check if there's only the 'page' parameter
  return params && params.has('page') && params.keys().length === 1;
};

export const getTileHeight = (element, rowProps) => {
  if (!element?.firstElementChild) return { tileHeightValue: 0, tileBadgeHeightValue: 0 };

  let shouldAllowMaxHeight = false;
  if (!isEmpty(rowProps)) {
    const { rowEnd, currentIndex } = rowProps;
    if (currentIndex >= rowEnd) shouldAllowMaxHeight = true;
  }
  const tile = element.firstElementChild;
  return {
    tileHeightValue: tile.clientHeight || 0,
    tileBadgeHeightValue: tile.children[0]?.clientHeight || 0,
    shouldAllowMaxHeight,
  };
};

/**
 * Builds an array of displayable L3 categories for filters
 *
 * @param {Object} bucketList - contains available L3 categories with count, id, name
 * @param {Object} currentCatList - contains subCategories with displayToCustomer and seoToken
 * @returns {Array} - array of objects: { count, id, name, seoToken }
 */
export const getSubCategoryForFiltering = (bucketList, currentCatList) => {
  const availableCategories = bucketList?.multilevel?.bucket?.[0]?.values || [];
  const subCategories =
    currentCatList?.catDepth === 'l2' && currentCatList?.hasL3 ? currentCatList.subCategories : [];

  if (!availableCategories.length || !subCategories.length) return [];

  // Create a Map for O(1) subcategory lookup
  const subCategoryMap = new Map(
    subCategories.map((subCat) => [String(subCat.categoryContent?.id), subCat])
  );

  return availableCategories.reduce((acc, { count, id, name }) => {
    const matched = subCategoryMap.get(String(id));
    if (matched?.categoryContent?.displayToCustomer) {
      acc.push({
        count,
        id,
        name,
        seoToken: matched.categoryContent.seoToken,
      });
    }
    return acc;
  }, []);
};

export const filterFacetValues = (updatedFacets, filterCatGrp = []) => {
  if (!updatedFacets?.multilevel?.bucket?.length) return updatedFacets;

  return {
    ...updatedFacets,
    multilevel: {
      ...updatedFacets.multilevel,
      bucket: updatedFacets.multilevel.bucket.map((bucket) => ({
        ...bucket,
        values: Array.isArray(bucket.values)
          ? bucket.values.filter((v) => filterCatGrp.includes(v.name))
          : bucket.values,
      })),
    },
  };
};

export const getAppliedSubCatFilters = (filterIds = {}) => {
  const appliedSubCatFilters = { filter_cat_grp: [] };
  const facetKeys = filterIds ? Object.keys(filterIds) : [];

  facetKeys.forEach((facetKey) => {
    if (facetKey === 'filter_cat_grp') {
      appliedSubCatFilters[facetKey] = filterIds[facetKey];
    }
  });
  return appliedSubCatFilters;
};

export const mergeUpdatedFacets = (list, updatedFacets) => {
  if (!Array.isArray(list) || list.length === 0) return updatedFacets;

  return {
    ...updatedFacets,
    text: {
      ...updatedFacets.text,
      list,
    },
  };
};

export const getCommonObjects = (matchedLists) => {
  // pick the dataset with the fewest objects as base
  const base = matchedLists.reduce((smallest, current) =>
    current.length < smallest.length ? current : smallest
  );

  return base
    .map((baseItem) => {
      // get the same displayName object from all lists
      const commonEntries = matchedLists.map((list) =>
        list.find((item) => item.displayName === baseItem.displayName)
      );

      // skip if any dataset does not have this displayName
      if (commonEntries.some((entry) => !entry)) return null;

      // extract [string, number] pairs for each dataset
      const valueSets = commonEntries.map((entry) =>
        Array.from({ length: entry.values.length / 2 }, (_, i) => [
          entry.values[i * 2],
          entry.values[i * 2 + 1],
        ])
      );

      // find common values (intersection by string)
      const commonPairs = valueSets.reduce((acc, pairs) => {
        const map = new Map(pairs);
        return acc.filter(([str]) => map.has(str));
      });

      if (!commonPairs.length) return null;

      // flatten to ["MULTI", 12] format (take number from first dataset)
      const values = commonPairs.flat();

      return {
        displayName: baseItem.displayName,
        facetName: baseItem.facetName,
        filterField: baseItem.filterField,
        position: baseItem.position,
        values,
      };
    })
    .filter(Boolean);
};

export const updateCommonList = (commonList, l2List) => {
  return commonList.map((commonItem) => {
    const matchL2 = l2List.find((l2Item) => l2Item.displayName === commonItem.displayName);

    if (!matchL2) return commonItem;

    const updatedValues = [...commonItem.values];

    // Loop through values of common list in pairs [label, number]
    for (let i = 0; i < updatedValues.length; i += 2) {
      const label = updatedValues[i];
      const indexInL2 = matchL2.values.indexOf(label);

      if (indexInL2 !== -1 && indexInL2 + 1 < matchL2.values.length) {
        updatedValues[i + 1] = matchL2.values[indexInL2 + 1]; // replace count
      }
    }

    return { ...commonItem, values: updatedValues };
  });
};

export const getUpdatedFacetsList = (l3FacetMapList, appliedSubCatFilterVal, updatedFacets) => {
  const { filter_cat_grp: appliedFilter = [] } = appliedSubCatFilterVal || {};

  let list = null;

  // Case: single filter, one facet group
  if (appliedFilter.length === 1 && l3FacetMapList.length === 1) {
    const { list: listVal } = l3FacetMapList[0];
    list = listVal;
  }

  // Case: single filter, multiple facet groups
  if (appliedFilter.length === 1 && l3FacetMapList.length > 1) {
    const match = l3FacetMapList.find((item) => item.name === appliedFilter[0]);
    list = match ? match.list : [];
  }

  // Case: multiple filter, multiple facet groups
  if (appliedFilter.length > 1 && l3FacetMapList.length > 1) {
    const { text: { list: l2FacetList = [] } = {} } = updatedFacets ?? {};

    const matchedLists = l3FacetMapList
      .filter((item) => appliedFilter.includes(item.name))
      .map((item) => item.list);

    if (matchedLists.length === 0) return null;

    // Intersect dynamically across all matched lists
    const commonList = getCommonObjects(matchedLists);
    const updatedList = updateCommonList(commonList, l2FacetList);

    list = updatedList.length ? updatedList : null;
  }

  if (list) {
    return mergeUpdatedFacets(list, updatedFacets);
  }
  return updatedFacets;
};
